import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { FolderPlus, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { NewApplicationForm } from '../schemas/NewApplicationSchema'

type PostNewApplicationProps = {
  applicationKey: string
  title: string
  description: string
  tag: string[]
  auth: object
  requiresApiUser: boolean
  requiresEntityAccount: boolean
  metadata?: object | null
}

async function postNewApplication(data: PostNewApplicationProps) {
  return api.backend.post('/application', data)
}

export function useNewApplication() {
  const { t } = useTranslation('sso')

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    PostNewApplicationProps
  >({
    mutationKey: ['/application'],
    mutationFn: postNewApplication,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: FolderPlus,
          title: t('applications.drawer_new_application.toast_success.title'),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/application/paginate'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('applications.drawer_new_application.toast_error.title'),
          description:
            error?.response?.data.friendlyMessage ||
            t('applications.drawer_new_application.toast_error.description'),
        },
      })
    },
  })

  const handleCreateApplication = async (data: NewApplicationForm) => {
    const payload: PostNewApplicationProps = {
      applicationKey: data.applicationKey,
      title: data.title,
      description: data.description,
      tag: Array.isArray(data.tag)
        ? data.tag.map((tag) => tag.value || tag.label || '')
        : [],
      auth: JSON.parse(data.auth),
      requiresApiUser: data.needsApiUser,
      requiresEntityAccount: data.needsEntityAccount,
      metadata:
        data.needsEntityAccount && data.metadata
          ? JSON.parse(data.metadata)
          : null,
    }

    return mutateAsync(payload)
  }

  return {
    handleCreateApplication,
    isCreatingApplication: isPending,
  }
}
